import React, { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  Button,
  InputAdornment,
} from '@material-ui/core';
import { styled } from '@material-ui/styles';

import Icon from 'components/Icon';
import { getProductsUniversal } from 'actions/productsDB';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { Product } from 'types/Product';
import { RootState } from 'reducers';

type ProductSearchProps = {
  handleProductSelect: (productID: Product['productID']) => void;
  productType: 'donation' | 'tip';
  selectedProductId?: Product['productID'];
};
/**
 * In case name is too long, the table becomes too wide and it looks bad, thus name column is limited to the width of the row and the text is cut
 */
const NameTableCell = styled(TableCell)({
  maxWidth: '250px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const ProductSearch: FC<ProductSearchProps> = ({
  handleProductSelect,
  productType,
  selectedProductId = 0,
}) => {
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const [products, setProducts] = useState<Product[]>([]);
  const [searchText, setSearchText] = useState('');
  const warehouseID = useSelector(getSelectedWarehouseID);
  const { t } = useTranslation('customerDisplay');

  const searchProducts = async () => {
    const { products: matchingProducts } = await dispatch(
      getProductsUniversal({
        orderBy: 'price',
        recordsOnPage: 100,
        searchNameIncrementally: searchText,
        orderByDir: 'asc',
        getProductsFor: 'SALES',
        warehouseID,
      }),
    );
    setProducts(matchingProducts);
  };

  const productIsDiscountable = (p: Product) => p.nonDiscountable === 0;
  const productIsZeroPriced = (p: Product) => Number(p.price) === 0;
  const productIsRefundable = (p: Product) => p.nonRefundable === 0;
  const productIsTaxFree = (p: Product) => p.taxFree;
  const showSelectButton = (p: Product) => {
    return (
      !productIsDiscountable(p) &&
      productIsZeroPriced(p) &&
      productIsTaxFree(p) &&
      !productIsRefundable(p)
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <>
      <form
        onSubmit={async e => {
          e.preventDefault();
          await searchProducts();
        }}
        style={{ marginTop: '15px' }}
      >
        {/* TODO: Add a label instead of this */}
        {t(`${productType}Form.searchProductLabel`)}
        <br />
        {t(`${productType}Form.searchProductLabel2`)}
        <TextField
          fullWidth
          variant="outlined"
          onChange={handleChange}
          value={searchText}
          data-testid="search-text"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={searchProducts}
                  data-testid="search-btn"
                >
                  {t('buttons.search')}
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </form>
      {/** TableContainer fixes an issue where if the table is too wide, on scrolling the component would become misaligned */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead style={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell style={{ width: '250px' }}>
                {t('productsTable.headers.name')}
              </TableCell>
              <TableCell style={{ width: '200px' }}>
                {t('productsTable.headers.code')}
              </TableCell>
              <TableCell style={{ width: '200px' }}>
                {t('productsTable.headers.notDiscountable')}
              </TableCell>
              <TableCell style={{ width: '200px' }}>
                {t('productsTable.headers.zeroPriced')}
              </TableCell>
              <TableCell style={{ width: '200px' }}>
                {t('productsTable.headers.notRefundable')}
              </TableCell>
              <TableCell style={{ width: '200px' }}>
                {t('productsTable.headers.taxFree')}
              </TableCell>
              <TableCell style={{ width: '250px' }}>
                {t('productsTable.headers.setAs', { context: productType })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(p => (
              <TableRow
                key={p.productID}
                data-testid="product"
                data-test-key={p.productID}
              >
                <NameTableCell>{p.name}</NameTableCell>
                <TableCell>{p.code}</TableCell>
                <TableCell>
                  <Icon
                    name={
                      productIsDiscountable(p) ? 'icon_close' : 'icon_check'
                    }
                    style={{
                      color: productIsDiscountable(p) ? 'red' : 'green',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Icon
                    name={!productIsZeroPriced(p) ? 'icon_close' : 'icon_check'}
                    style={{
                      color: !productIsZeroPriced(p) ? 'red' : 'green',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Icon
                    name={productIsRefundable(p) ? 'icon_close' : 'icon_check'}
                    style={{
                      color: productIsRefundable(p) ? 'red' : 'green',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Icon
                    name={!productIsTaxFree(p) ? 'icon_close' : 'icon_check'}
                    style={{
                      color: !productIsTaxFree(p) ? 'red' : 'green',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={
                      !showSelectButton(p) || p.productID === selectedProductId
                    }
                    fullWidth
                    onClick={() => handleProductSelect(p.productID)}
                  >
                    {t(
                      `productsTable.actions.${
                        p.productID === selectedProductId ? 'selected' : 'set'
                      }`,
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
